/**
 * Internal API Client — Cognito + SigV4 for Agent Platform Gateway.
 *
 * Auth flow:
 *   Federate OIDC id_token → Cognito Identity Pool (us-west-2)
 *   → IAM temp credentials → SigV4 signed request (service: "execute-api")
 *   → Internal API Gateway → Gateway Fargate service
 */

import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { appConfig, FEDERATE } from '../config';

const INTERNAL_API_REGION = 'us-west-2';

// ── Types ───────────────────────────────────────────────────────────────────

interface AwsCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
  expiration?: Date;
}

export interface AuthCallbackResult {
  status: string;
  message: string;
  sessionId?: string;
  channel?: string;
  alias?: string;
  error?: string;
}

// ── Cognito credentials (cached) ────────────────────────────────────────────

let cachedCreds: { credentials: AwsCredentials; expiration: number; token: string } | null = null;

function getCookie(key: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${key}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

async function getCredentials(): Promise<AwsCredentials> {
  const federateToken = getCookie(FEDERATE.idTokenKey);
  if (!federateToken) {
    throw new Error('No Federate ID token — user not authenticated');
  }

  if (
    cachedCreds &&
    cachedCreds.token === federateToken &&
    cachedCreds.expiration > Date.now() + 120_000
  ) {
    return cachedCreds.credentials;
  }

  const provider = fromCognitoIdentityPool({
    identityPoolId: appConfig.identityPoolId,
    clientConfig: { region: INTERNAL_API_REGION },
    logins: {
      [appConfig.identityProvider]: federateToken,
    },
  });

  const creds = await provider();
  cachedCreds = {
    credentials: creds,
    expiration: creds.expiration?.getTime() ?? Date.now() + 3600_000,
    token: federateToken,
  };
  return creds;
}

// ── SigV4 signing (manual — avoids heavy @smithy/signature-v4 dep) ──────────
// Using dynamic import to keep the main bundle light

async function signedFetch(
  url: string,
  method: string,
  body?: string,
): Promise<Response> {
  // Lazy-load signing utilities
  const [{ SignatureV4 }, { Sha256 }, { HttpRequest }] = await Promise.all([
    import('@smithy/signature-v4'),
    import('@aws-crypto/sha256-js'),
    import('@smithy/protocol-http'),
  ]);

  const creds = await getCredentials();
  const parsed = new URL(url);

  const headers: Record<string, string> = {
    host: parsed.hostname,
  };
  if (body) {
    headers['content-type'] = 'application/json';
  }

  const request = new HttpRequest({
    method,
    protocol: 'https:',
    hostname: parsed.hostname,
    path: parsed.pathname,
    query: Object.fromEntries(parsed.searchParams.entries()),
    headers,
    body: body ?? undefined,
  });

  const signer = new SignatureV4({
    credentials: creds,
    region: INTERNAL_API_REGION,
    service: 'execute-api',
    sha256: Sha256,
  });

  const signed = await signer.sign(request);

  // Remove 'host' header — browser sets it automatically
  const fetchHeaders: Record<string, string> = {};
  for (const [key, value] of Object.entries(signed.headers)) {
    if (key.toLowerCase() !== 'host') {
      fetchHeaders[key] = value as string;
    }
  }

  return fetch(url, {
    method,
    headers: fetchHeaders,
    body: body ?? undefined,
  });
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Call the internal auth callback API to resume an interrupted agent session.
 *
 * @param stateToken - The state token from the login URL (DDB key)
 * @param token - The Federate ID token
 * @returns The API response with resume status
 */
export async function authCallback(
  stateToken: string,
  token: string,
): Promise<{ statusCode: number; body: AuthCallbackResult }> {
  const url = `${appConfig.internalUrl}/internal/auth/callback`;

  const resp = await signedFetch(
    url,
    'POST',
    JSON.stringify({ state: stateToken, token }),
  );

  let respBody: AuthCallbackResult;
  try {
    respBody = await resp.json();
  } catch {
    respBody = { status: 'error', message: `Unexpected response (${resp.status})` } as AuthCallbackResult;
  }
  return { statusCode: resp.status, body: respBody };
}
